interface Project {
  id: string;
  name: string;
  contextOfPurpose: string;
  description?: string;
  timeHorizon?: string;
}

interface Driver {
  id: string;
  title?: string;
  narrative: string;
  currentState: string;
  problemOpportunity: string;
  ramifications: string;
  desiredOutcome?: string;
  order: number;
}

interface Assumption {
  id: string;
  text: string;
}

interface OpenQuestion {
  id: string;
  text: string;
}

interface Stakeholder {
  id: string;
  name: string;
  role?: string;
  notes?: string;
}

export function exportSet(data: {
  project: Project | null;
  drivers: Driver[];
  assumptions: Assumption[];
  openQuestions: OpenQuestion[];
  stakeholders: Stakeholder[];
}) {
  const markdown = generateSetMarkdown(data);
  downloadMarkdown(markdown);
}

function generateSetMarkdown(data: {
  project: Project | null;
  drivers: Driver[];
  assumptions: Assumption[];
  openQuestions: OpenQuestion[];
  stakeholders: Stakeholder[];
}): string {
  let md = '# Outcome Driver Set\n\n';

  if (data.project) {
    md += `## ${data.project.name}\n\n`;
    md += `### Context of Purpose\n\n${data.project.contextOfPurpose}\n\n`;
    if (data.project.timeHorizon) {
      md += `**Time Horizon:** ${data.project.timeHorizon}\n\n`;
    }
    if (data.project.description) {
      md += `**Description:** ${data.project.description}\n\n`;
    }
  }

  // Drivers
  md += `## Outcome Drivers\n\n`;
  const sortedDrivers = [...data.drivers].sort((a, b) => a.order - b.order);
  sortedDrivers.forEach((driver, index) => {
    md += `### ${driver.title || `Driver ${index + 1}`}\n\n`;
    md += `${driver.narrative}\n\n`;
    
    if (driver.desiredOutcome) {
      md += `**Desired Outcome:** ${driver.desiredOutcome}\n\n`;
    }
    
    md += `#### Components\n\n`;
    md += `**Current State:** ${driver.currentState}\n\n`;
    md += `**Problem / Opportunity:** ${driver.problemOpportunity}\n\n`;
    md += `**Ramifications:** ${driver.ramifications}\n\n`;
    md += `---\n\n`;
  });

  // Supporting Artifacts
  if (data.assumptions.length > 0) {
    md += `## Assumptions\n\n`;
    data.assumptions.forEach(assumption => {
      md += `- ${assumption.text}\n`;
    });
    md += `\n`;
  }

  if (data.openQuestions.length > 0) {
    md += `## Open Questions\n\n`;
    data.openQuestions.forEach(question => {
      md += `- ${question.text}\n`;
    });
    md += `\n`;
  }

  if (data.stakeholders.length > 0) {
    md += `## Stakeholders\n\n`;
    data.stakeholders.forEach(stakeholder => {
      md += `### ${stakeholder.name}\n`;
      if (stakeholder.role) {
        md += `**Role:** ${stakeholder.role}\n`;
      }
      if (stakeholder.notes) {
        md += `${stakeholder.notes}\n`;
      }
      md += `\n`;
    });
  }

  md += `---\n\n*Generated on ${new Date().toLocaleDateString()}*\n`;

  return md;
}

function downloadMarkdown(markdown: string) {
  const blob = new Blob([markdown], { type: 'text/markdown' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `outcome-driver-set-${Date.now()}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}


